import { getAuthToken } from '@dynamic-labs/sdk-react-core';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { DocumentNode } from 'graphql';

import { sendGraphqlRequest } from '../graphql';

type UseDatabaseQueryOptions = {
  queryKey: unknown[];
  query: DocumentNode;
  variables: Record<string, any>;
  field: string;
  enabled?: boolean;
  options?: Record<string, any>;
  headers?: Record<string, string>;
};

/**
 * Custom hook to perform a GraphQL query using React Query.
 * @function useDatabaseQuery
 * @template T - The type of the response data.
 * @param {UseDatabaseQueryOptions} options - The options for the GraphQL query.
 * @param {unknown[]} options.queryKey - The query key for React Query.
 * @param {DocumentNode} options.query - The GraphQL query document.
 * @param {Record<string, any>} options.variables - The variables for the GraphQL query.
 * @param {string} options.field - The field to extract from the response data.
 * @param {boolean} [options.enabled=true] - Whether the query is enabled.
 * @param {Record<string, any>} [options.options] - Additional options for React Query.
 * @param {Record<string, string>} [options.headers] - Optional headers for the request.
 * @returns {UseQueryResult<T>} The result of the query.
 */
export const useDatabaseQuery = <T = any>({
  queryKey,
  query,
  variables,
  field,
  enabled = true,
  options,
  headers,
}: UseDatabaseQueryOptions): UseQueryResult<T> =>
  useQuery({
    queryKey,
    queryFn: () =>
      sendGraphqlRequest<T>({
        headers,
        query,
        field,
        variables,
        authToken: getAuthToken(),
      }),
    enabled,
    ...options,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
